import React, { useState, useEffect, useCallback } from 'react';
import { fetchBalanceSheet, exportReport } from '../api';

export default function BalanceSheetReport({ authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asAt, setAsAt] = useState(new Date().toISOString().slice(0, 10));
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchBalanceSheet(authAxios, { as_at: asAt });
      setData(result);
    } catch (e) {
      console.error('Failed to load balance sheet', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, asAt]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const blob = await exportReport(authAxios, {
        report: 'balance-sheet', format: fmt, as_at: asAt,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `balance_sheet_${asAt}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const fmt = (v) => v != null ? Number(v).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  const Section = ({ title, rows, total, totalLabel }) => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b hover:bg-gray-50">
              <td className="px-4 py-1.5 font-mono w-24">{r.code}</td>
              <td className="px-4 py-1.5">{r.name}</td>
              <td className="px-4 py-1.5 text-xs text-gray-400">{r.sub_category}</td>
              <td className="px-4 py-1.5 text-right w-36">{fmt(r.balance)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-bold bg-gray-50">
            <td colSpan={3} className="px-4 py-2">{totalLabel || `Total ${title}`}</td>
            <td className="px-4 py-2 text-right">{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Balance Sheet</h2>
        <div className="flex gap-2">
          <button onClick={() => handleExport('xlsx')} disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">
            Excel
          </button>
          <button onClick={() => handleExport('pdf')} disabled={exporting}
            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50">
            PDF
          </button>
        </div>
      </div>

      <div className="flex gap-4 items-end bg-gray-50 p-4 rounded-lg">
        <div>
          <label className="block text-sm text-gray-600 mb-1">As At</label>
          <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {data && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">Statement of Financial Position as at {data.as_at}</p>
          <Section title="Assets" rows={data.assets} total={data.total_assets} />
          <Section title="Liabilities" rows={data.liabilities} total={data.total_liabilities} />
          <Section title="Equity" rows={data.equity} total={data.total_equity} />

          <div className="border-t-2 pt-4 flex justify-between items-center">
            <div className="space-y-1">
              <p className="font-bold">Total Liabilities + Equity: {fmt(data.total_liabilities_and_equity)}</p>
              <p className="font-bold">Total Assets: {fmt(data.total_assets)}</p>
            </div>
            <span className={`px-3 py-1 rounded text-sm font-medium ${data.is_balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {data.is_balanced ? 'Balanced' : 'UNBALANCED'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
